import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import apiClient from "../../lib/api-client";

/**
 * Hook for common/metadata lookup data used across the app
 */
export const useBrokers = () => {
    return useQuery({
        queryKey: ["stock-brokers"],
        queryFn: async () => {
            const response = await apiClient.get("/stock-brokers");
            return response.data?.data || [];
        },
        staleTime: 1000 * 60 * 60, // Brokers don't change often, cache for 1 hour
    });
};

export const useNepseData = () => {
    return useQuery({
        queryKey: ["nepse-data"],
        queryFn: async () => {
            const response = await apiClient.get("/getNepseData");
            return response.data;
        },
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });
};

export const useCompanyData = () => {
    return useQuery({
        queryKey: ["company-data"],
        queryFn: async () => {
            const response = await apiClient.get("/getCompanyData");
            return response.data?.data || {};
        },
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });
};

export const useUsersList = (options = {}) => {
    return useQuery({
        queryKey: ["users-list"],
        queryFn: async () => {
            const response = await apiClient.post("/usersList");
            return response.data?.data || [];
        },
        staleTime: 1000 * 60 * 10, // Cache for 10 minutes
        ...options,
    });
};

export const useShareSummary = () => {
    return useQuery({
        queryKey: ["share-summary-calc"],
        queryFn: async () => {
            const response = await apiClient.get("/fetchDataAndCalculate");
            return response.data?.data || { totalAmountSum: 0, totalMarketValue: 0, totalProfitLoss: 0 };
        },
        staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    });
};

export const useExpiringFDs = (params = {}) => {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + 7);

    const defaultParams = {
        page: 1,
        rowsPerpage: 20,
        from_date: today.toISOString().split("T")[0],
        to_date: futureDate.toISOString().split("T")[0],
        ...params
    };

    return useQuery({
        queryKey: ["fd-expiring", defaultParams],
        queryFn: async () => {
            const response = await apiClient.get("/gotoFutureExpiringId", { params: defaultParams });
            return response.data;
        },
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });
};

export const useRoles = () => {
    return useQuery({
        queryKey: ["roles"],
        queryFn: async () => {
            const response = await apiClient.get("/roles");
            return response.data || [];
        },
        staleTime: 1000 * 60 * 30, // Roles don't change often
    });
};

export const useShareInitialDetails = () => {
    return useQuery({
        queryKey: ["share-initial-details"],
        queryFn: async () => {
            const response = await apiClient.post("/getshareinitialDetails");
            return response.data || { particulars: [], sharetypes: [] };
        },
        staleTime: 1000 * 60 * 15, // Cache for 15 minutes
    });
};

export const useInfiniteExpiringFDs = (params = {}) => {
    const today = new Date();
    const from_date = today.toISOString().split("T")[0];
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + 7);
    const to_date = futureDate.toISOString().split("T")[0];

    const baseParams = {
        rowsPerpage: 20,
        from_date,
        to_date,
        showExpired: true,
        ...params
    };

    return useInfiniteQuery({
        queryKey: ["fd-expiring-infinite", baseParams],
        queryFn: async ({ pageParam = 1 }) => {
            const response = await apiClient.get("/gotoFutureExpiringId", {
                params: { ...baseParams, page: pageParam }
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            const data = lastPage?.data;
            if (data?.current_page < data?.last_page) {
                return data.current_page + 1;
            }
            return undefined;
        },
        staleTime: 1000 * 60 * 5,
    });
};
